import type {
	UploadStandardKey,
	UploadTemplateOption,
} from "@/core/components/upload-wizard";
import {
	BanknoteIcon,
	FileSpreadsheetIcon,
	type LucideIcon,
} from "lucide-react";

/**
 * Per-import-type configuration consumed by the generic ImportContainer.
 *
 * The Import page is templateType-driven: it reads `templateType` from the
 * URL, looks up an entry here, and feeds the result into the shared
 * UploadWizard. Add a new entry to enable a new flow without forking the
 * page.
 *
 * `onCompleteToast` + `returnTo` carry the domain side-effect for now —
 * once the per-domain mutations exist (recon bank → reconcile,
 * transfer_order → bulk import), each entry's container-time handler can
 * call them inside the wrapping ImportContainer.
 */
export interface ImportTemplateConfig {
	/** The standard keys this flow's importer needs filled in. */
	standardKeys: UploadStandardKey[];
	/** Step 1 hero copy. */
	uploadHeading: string;
	uploadSubheading: string;
	/** Label on the saved-source picker. */
	uploadSourceLabel: string;
	newSourceLabel: string;
	continueLabelNew?: string;
	continueLabelExisting?: string;
	requiredSubtitle?: string;
	sourceNamePlaceholder?: string;
	/** Step 3 primary-button copy. */
	completeLabel?: string;
	/** Icon for step 1's gradient card. */
	HeaderIcon?: LucideIcon;
	/** Page title rendered above the wizard. */
	pageTitle: string;
	/** Toast message on successful step-3 completion (pre-mutation hook-up). */
	onCompleteToast: string;
	/** Where to navigate after step 3 finishes — back to the list page. */
	returnTo: string;
}

// Mandatory per product spec: Transaction Date, Amount, Merchant ID,
// Approval Code, Settlement Date.
const RECON_BANK_KEYS: UploadStandardKey[] = [
	{
		key: "transactionDate",
		label: "Transaction Date",
		required: true,
		hint: "Date the bank logged the transaction",
	},
	{
		key: "amount",
		label: "Amount",
		required: true,
		hint: "Gross transaction amount",
	},
	{
		key: "merchantID",
		label: "Merchant ID",
		required: true,
		hint: "Resolves the outlet via masterdata MID → branch.",
	},
	{
		key: "approvalCode",
		label: "Approval Code",
		required: true,
		hint: "EDC approval / auth code.",
	},
	{
		key: "settlementDate",
		label: "Settlement Date",
		required: true,
		hint: "Date the bank settled (paid out) the transaction.",
	},
	{
		key: "mdrAmount",
		label: "MDR",
		// Optional: some statements (Grab) have no fee column; unmapped MDR is stored as 0.
		required: false,
		hint: "MDR / fee amount from the file.",
	},
	{
		key: "referenceNumber",
		label: "Reference Number",
		required: false,
		hint: "QRIS reference / Reff ID. QRIS files match on its last 6 characters.",
	},
	{
		key: "transactionTime",
		label: "Transaction Time",
		required: false,
		hint: "Transaction time (e.g. HHMMSS or HH:MM:SS). Lets the matcher pair on amount + time + branch when the reference can't align (e.g. Mandiri QRIS/EDC) — big lift over reference-only.",
	},
	{
		key: "netAmount",
		label: "Net Amount",
		required: false,
		hint: "Net amount paid out (gross − MDR).",
	},
	{
		key: "merchantName",
		label: "Merchant Name",
		required: false,
		hint: "Outlet / trading name on the statement.",
	},
	{
		key: "terminalID",
		label: "Terminal ID",
		required: false,
		hint: "EDC terminal (TID).",
	},
	{
		key: "cardNumber",
		label: "Card Number",
		required: false,
		hint: "Masked card / PAN (EDC).",
	},
	{
		key: "issuer",
		label: "Issuer",
		required: false,
		hint: "Issuing bank / wallet (e.g. GOPAY, BCA).",
	},
];

const TRANSFER_ORDER_KEYS: UploadStandardKey[] = [
	{
		key: "branchCode",
		label: "Branch Code",
		required: true,
		hint: "Non-empty cell marks the start of a new TO; subsequent rows with this column blank are appended as items. Resolved via masterdata.branch.branch_code.",
	},
	{
		key: "fromWarehouseCode",
		label: "From Warehouse Code",
		required: true,
		hint: "Resolved via masterdata.warehouse.warehouse_code",
	},
	{
		key: "toWarehouseCode",
		label: "To Warehouse Code",
		required: true,
		hint: "Must differ from From Warehouse",
	},
	{
		key: "transferOrderDate",
		label: "Transfer Order Date",
		required: true,
		hint: "Format YYYY-MM-DD",
	},
	{
		key: "scheduledDeliveryDate",
		label: "Scheduled Delivery Date",
		required: true,
		hint: "Format YYYY-MM-DD",
	},
	{ key: "noteInternal", label: "Note Internal", required: false },
	{
		key: "noteForReceiver",
		label: "Note For Receiver",
		required: true,
		hint: "Required — note shown to the receiving warehouse on the TO.",
	},
	{
		key: "productCode",
		label: "Barcode",
		required: true,
		hint: "Resolved via masterdata product_barcode_map (barcode / alias).",
	},
	{ key: "qty", label: "Qty", required: true },
	{
		key: "uomName",
		label: "UoM Name",
		required: true,
		hint: "Required — matched per product's UoM list. TO creation rejects items without one.",
	},
	{ key: "description", label: "Description", required: false },
];

const REPLENISHMENT_TEMPLATE_ITEM_KEYS: UploadStandardKey[] = [
	{
		key: "productCode",
		label: "Product Code",
		required: true,
		hint: "Resolved via masterdata.product.product_code",
	},
	{ key: "minStock", label: "Min Stock", required: true },
	{ key: "orderMultiplier", label: "Order Multiplier", required: true },
	{ key: "leadtimeDays", label: "Leadtime (Days)", required: true },
	{ key: "safetyDays", label: "Safety Days", required: true },
];

const PROMO_COMPONENT_KEYS: UploadStandardKey[] = [
	{
		key: "productCode",
		label: "Barcode",
		required: true,
		hint: "Resolved via product_barcode_map (barcode / alt barcode). Product code still works as a fallback.",
	},
	{
		key: "productName",
		label: "Product Name",
		required: false,
		hint: "Reference only — never read by the importer.",
	},
	{
		key: "quotaMaxQty",
		label: "Quota Max Qty",
		required: false,
		hint: "CAT1 / CAT3 only. Required on every row when Use Quota is on.",
	},
];

const PROMO_COMPONENT_NAME_KEY: UploadStandardKey = {
	key: "componentName",
	label: "Component Name",
	required: true,
	hint: "Rows sharing this name become one component. Renaming it creates a new component and leaves the old one alive \u2014 rename in the UI instead.",
};

const PROMO_COMPONENT_PRODUCT_KEYS: UploadStandardKey[] = [
	{
		key: "productCode",
		label: "Barcode",
		required: true,
		hint: "Resolved via product_barcode_map (barcode / alt barcode). Product code still works as a fallback.",
	},
	{
		key: "productName",
		label: "Product Name",
		required: false,
		hint: "Reference only \u2014 never read by the importer.",
	},
];

const PROMO_COMPONENT_CAT1_KEYS: UploadStandardKey[] = [
	PROMO_COMPONENT_NAME_KEY,
	{
		key: "discountPercent",
		label: "Discount Percent",
		required: true,
		hint: "Belongs to the component \u2014 repeat it on every row, or fill the first row only.",
	},
	...PROMO_COMPONENT_PRODUCT_KEYS,
	{
		key: "quotaMaxQty",
		label: "Quota Max Qty",
		required: false,
		hint: "One limit per product. Filling any row turns quota on for this component.",
	},
];

const PROMO_COMPONENT_CAT2_KEYS: UploadStandardKey[] = [
	PROMO_COMPONENT_NAME_KEY,
	{
		key: "minQty",
		label: "Min Qty",
		required: true,
		hint: "Belongs to the component \u2014 repeat it on every row, or fill the first row only.",
	},
	{
		key: "freeQty",
		label: "Free Qty",
		required: true,
		hint: "Belongs to the component \u2014 repeat it on every row, or fill the first row only.",
	},
	{
		key: "isMultiple",
		label: "Multiple",
		required: false,
		hint: "TRUE/FALSE, YES/NO, Y/N, 1/0 or ya/tidak. Blank means FALSE.",
	},
	{
		key: "quotaMaxQty",
		label: "Quota Max Qty",
		required: false,
		hint: "One limit for the whole component, not per product. Filling it turns quota on.",
	},
	{
		key: "role",
		label: "Role",
		required: true,
		hint: "BUY or FREE, case-insensitive. Tells the two product lists apart; the row counts need not match.",
	},
	...PROMO_COMPONENT_PRODUCT_KEYS,
];

const PROMO_COMPONENT_CAT3_KEYS: UploadStandardKey[] = [
	PROMO_COMPONENT_NAME_KEY,
	{
		key: "discountAmount",
		label: "Discount Amount",
		required: true,
		hint: "Belongs to the component \u2014 repeat it on every row, or fill the first row only.",
	},
	...PROMO_COMPONENT_PRODUCT_KEYS,
	{
		key: "quotaMaxQty",
		label: "Quota Max Qty",
		required: false,
		hint: "One limit per product. Filling any row turns quota on for this component.",
	},
];

// Columns differ per category, so the promo type picked in the form decides which template downloads.
export function getPromoComponentTemplate(
	promoTypeName: string,
): { fileName: string; standardKeys: UploadStandardKey[] } | null {
	switch (promoTypeName) {
		case "CAT1_BUY_PRODUCT_X_GET_DISCOUNT_Y_PERCENT":
			return {
				fileName: "promo_components_cat1.csv",
				standardKeys: PROMO_COMPONENT_CAT1_KEYS,
			};
		case "CAT2_BUY_PRODUCT_X_GET_PRODUCT_Y":
			return {
				fileName: "promo_components_cat2.csv",
				standardKeys: PROMO_COMPONENT_CAT2_KEYS,
			};
		case "CAT3_BUY_PRODUCT_X_GET_DISCOUNT_XXXXX":
			return {
				fileName: "promo_components_cat3.csv",
				standardKeys: PROMO_COMPONENT_CAT3_KEYS,
			};
		default:
			return null;
	}
}

const PRICELIST_PRODUCT_KEYS: UploadStandardKey[] = [
	{
		key: "productCode",
		label: "Barcode",
		required: true,
		hint: "Resolved via masterdata product_barcode_map (barcode / alt barcode). Product code still works as a fallback.",
	},
	{
		key: "sellingPrice",
		label: "Selling Price",
		required: true,
		hint: "Must be > 0. Existing (period, product) rows are upserted.",
	},
];

const PRODUCT_AVAILABILITY_TEMPLATE_ITEM_KEYS: UploadStandardKey[] = [
	{
		key: "productCode",
		label: "Product Code",
		required: true,
		hint: "Resolved via masterdata.product.product_code. Rows for products already in this template are skipped.",
	},
	{
		key: "salesChannels",
		label: "Sales Channels",
		required: false,
		hint: "Channel names separated by ; (e.g. GrabFood;GrabMart). GrabMart uses taxonomy; GrabFood also needs Menu Group + Category. POS is implicit.",
		includeInTemplate: true,
		templateNote: "semicolon-separated",
	},
	{
		key: "menuGroup",
		label: "Menu Group",
		required: false,
		hint: "Menu group name — required when Sales Channels includes GrabFood (for the GrabFood placement).",
		includeInTemplate: true,
	},
	{
		key: "category",
		label: "Category",
		required: false,
		hint: "Category name within the menu group — required with GrabFood.",
		includeInTemplate: true,
	},
];

const INVENTORY_COUNT_ITEM_TEMPLATE_ITEM_KEYS: UploadStandardKey[] = [
	{
		key: "productCode",
		label: "Product Code",
		required: true,
		hint: "Resolved via masterdata.product.product_code. Rows for products already in this template are skipped.",
	},
];

const INVENTORY_ADJUSTMENT_OPENING_BALANCE_KEYS: UploadStandardKey[] = [
	{
		key: "productCode",
		label: "Barcode",
		required: true,
		hint: "Resolved via masterdata product_barcode_map (barcode / alias).",
	},
	{
		key: "uomName",
		label: "UoM Name",
		required: true,
		hint: "Resolved per product's UoM category. Pcs / Dus / Lusin / Box / etc.",
	},
	{
		key: "qty",
		label: "Qty",
		required: true,
		hint: "Must be != 0. Opening balance should set a real on-hand stock.",
	},
	{
		key: "unitCost",
		label: "Unit Cost",
		required: false,
		hint: "Cost per unit. Defaults to 0 if empty.",
	},
	{
		key: "locationName",
		label: "Warehouse Location Name",
		required: true,
		hint: "Resolved within the picked warehouse.",
	},
	{ key: "note", label: "Note", required: false },
];

const INVENTORY_ADJUSTMENT_COST_KEYS: UploadStandardKey[] = [
	{
		key: "productCode",
		label: "Barcode",
		required: true,
		hint: "Resolved via masterdata product_barcode_map (barcode / alias).",
	},
	{
		key: "unitCost",
		label: "Unit Cost",
		required: true,
		hint: "Target average unit cost to set for this product in the warehouse. On-hand qty is unchanged.",
	},
];

const PURCHASE_ORDER_KEYS: UploadStandardKey[] = [
	{
		key: "vendorCode",
		label: "Vendor Code",
		required: true,
		hint: "Non-empty cell marks the start of a new PO; subsequent rows with this column blank are appended as items to the same PO. Resolved via masterdata.vendor.vendor_code.",
	},
	{
		key: "branchCode",
		label: "Branch Code",
		required: true,
		hint: "Resolved via masterdata.branch.branch_code",
	},
	{
		key: "destinationWarehouseCode",
		label: "Destination Warehouse Code",
		required: true,
		hint: "Resolved via masterdata.warehouse.warehouse_code",
	},
	{
		key: "poDate",
		label: "PO Date",
		required: true,
		hint: "Format YYYY-MM-DD or DD/MM/YYYY",
	},
	{
		key: "deliveryDate",
		label: "Delivery Date",
		required: false,
		hint: "Optional — YYYY-MM-DD or DD/MM/YYYY. Defaults to PO Date + 7 days when blank.",
	},
	{
		key: "deadlineDate",
		label: "Deadline Date",
		required: false,
		hint: "Optional — YYYY-MM-DD or DD/MM/YYYY. Defaults to PO Date + 7 days when blank.",
	},
	{
		key: "vendorRefNo",
		label: "Vendor Ref No",
		required: false,
		hint: "External vendor reference (PO number on their side)",
	},
	{
		key: "purchaseAreaName",
		label: "Purchase Area",
		required: true,
		hint: "Required — resolved via masterdata.purchase_area.purchase_area_name (case-insensitive). PO creation rejects rows without one.",
	},
	// Tax Included is resolved from the vendor's purchasing info (masterdata), not the file.
	{
		key: "poDiscountType",
		label: "PO Discount Type",
		required: false,
		hint: "% or Rp",
	},
	{
		key: "poDiscountValue",
		label: "PO Discount Value",
		required: false,
	},
	{ key: "landedCost", label: "Landed Cost", required: false },
	{ key: "noteInternal", label: "Note Internal", required: false },
	{ key: "noteForVendor", label: "Note For Vendor", required: false },
	{
		key: "productCode",
		label: "Barcode",
		required: true,
		hint: "Resolved via masterdata product_barcode_map (barcode / alias).",
	},
	{ key: "qty", label: "Qty", required: true },
	// UoM is resolved from the product's purchasing UoM (masterdata), not the file.
	{
		key: "unitPrice",
		label: "Unit Price",
		required: false,
		hint: "Optional — when blank, resolved from master/inventory (consignment: selling − commission; else last avg cost → last unit cost → purchase cost).",
	},
	{
		key: "itemDisc1",
		label: "Disc 1",
		required: false,
		includeInTemplate: true,
		hint: "Cascading discount % (tier 1). Disc 1..4 apply in sequence on the remaining amount (perc_plus / bertingkat).",
	},
	{
		key: "itemDisc2",
		label: "Disc 2",
		required: false,
		includeInTemplate: true,
		hint: "Cascading discount % (tier 2).",
	},
	{
		key: "itemDisc3",
		label: "Disc 3",
		required: false,
		includeInTemplate: true,
		hint: "Cascading discount % (tier 3).",
	},
	{
		key: "itemDisc4",
		label: "Disc 4",
		required: false,
		includeInTemplate: true,
		hint: "Cascading discount % (tier 4).",
	},
	// Tax Code (PPN) is resolved from the product's purchase tax (masterdata), not the file.
];

const TRANSFER_REQUEST_KEYS: UploadStandardKey[] = [
	{
		key: "branchCode",
		label: "Branch Code",
		required: true,
		hint: "Requesting store branch. Non-empty cell marks the start of a new TR; subsequent rows with this column blank are appended as items. Resolved via masterdata.branch.branch_code.",
	},
	{
		key: "fromWarehouseCode",
		label: "From Warehouse Code",
		required: true,
		hint: "Source (DC) warehouse the goods are requested from. Resolved via masterdata.warehouse.warehouse_code.",
	},
	{
		key: "toWarehouseCode",
		label: "To Warehouse Code",
		required: true,
		hint: "Destination (store) warehouse. Must differ from From Warehouse.",
	},
	{
		key: "trDate",
		label: "TR Date",
		required: true,
		hint: "Format YYYY-MM-DD or DD/MM/YYYY",
	},
	{
		key: "scheduledDeliveryDate",
		label: "Scheduled Delivery Date",
		required: false,
		hint: "Optional — YYYY-MM-DD or DD/MM/YYYY. Defaults to TR Date when blank.",
	},
	{ key: "noteInternal", label: "Note Internal", required: false },
	{ key: "noteForApprover", label: "Note For Approver", required: false },
	{
		key: "productCode",
		label: "Barcode",
		required: true,
		hint: "Resolved via masterdata product_barcode_map (barcode / alias).",
	},
	{
		key: "qty",
		label: "Qty",
		required: true,
		hint: "Quantity requested, in the product's base UoM.",
	},
	{ key: "itemNote", label: "Item Note", required: false },
];

// Product import: labels double as the template headers, so a file built from the
// downloaded template maps itself. Nothing is hard-required here because an update
// row only needs Product Code or Barcode; the guidance row in the template spells
// out which columns a *new* product cannot go without, and the backend enforces
// that per row.
const NEW_PRODUCT_NOTE = "mandatory (new product)";
const NEW_PRODUCT_HINT =
	"Mandatory for a new product; leave empty to keep the stored value.";

const productKey = (
	key: string,
	label: string,
	templateNote: string,
	hint?: string,
): UploadStandardKey => ({
	key,
	label,
	required: false,
	includeInTemplate: true,
	templateNote,
	hint,
});

const PRODUCT_IMPORT_IDENTITY_KEYS: UploadStandardKey[] = [
	productKey(
		"productCode",
		"Product Code",
		"optional (fill only to update)",
		"SKU of an existing product to update. Leave empty for new products — the SKU is generated.",
	),
	productKey("productName", "Product Name", NEW_PRODUCT_NOTE, NEW_PRODUCT_HINT),
	productKey("shortName", "Short Name", "optional"),
	productKey(
		"barcode",
		"Barcode",
		NEW_PRODUCT_NOTE,
		"Mandatory for a new product. Without Product Code, a barcode that already exists updates that product.",
	),
];

const PRODUCT_IMPORT_CLASSIFICATION_KEYS: UploadStandardKey[] = [
	productKey("source", "Source", NEW_PRODUCT_NOTE, NEW_PRODUCT_HINT),
	productKey("division", "Division", NEW_PRODUCT_NOTE, NEW_PRODUCT_HINT),
	productKey("department", "Department", NEW_PRODUCT_NOTE, NEW_PRODUCT_HINT),
	productKey(
		"subDepartment",
		"Subdepartment",
		NEW_PRODUCT_NOTE,
		NEW_PRODUCT_HINT,
	),
	productKey("category", "Category", NEW_PRODUCT_NOTE, NEW_PRODUCT_HINT),
	productKey("subCategory", "Subcategory", NEW_PRODUCT_NOTE, NEW_PRODUCT_HINT),
	productKey("brand", "Brand", NEW_PRODUCT_NOTE, NEW_PRODUCT_HINT),
	productKey(
		"productType",
		"Type",
		"optional (default inventory)",
		"Only inventory products can be imported.",
	),
	productKey(
		"uomCategory",
		"UoM Category",
		NEW_PRODUCT_NOTE,
		"Mandatory for a new product. Cannot be changed on an existing product.",
	),
	productKey(
		"baseUom",
		"Base UoM",
		NEW_PRODUCT_NOTE,
		"Mandatory for a new product. Cannot be changed on an existing product.",
	),
	productKey(
		"gram",
		"Gram",
		NEW_PRODUCT_NOTE,
		"Weight in grams, above 0. Mandatory for a new product.",
	),
	productKey(
		"trackExpiredDate",
		"Track Expired Date",
		"optional (default FALSE)",
		"TRUE / FALSE.",
	),
];

const PRODUCT_IMPORT_TRAILING_KEYS: UploadStandardKey[] = [
	productKey("country", "Country", "optional"),
	productKey(
		"productTag",
		"Product Tag",
		"optional",
		"One tag name. When filled it replaces all of the product's tags.",
	),
	productKey(
		"accountingCategory",
		"Product Accounting Category",
		"optional",
		"The ERP (Odoo) category name, exactly as the product form's Accounting Category dropdown lists it, e.g. GROCERY (PPN).",
	),
	productKey(
		"isActive",
		"Is Active",
		"optional (default TRUE)",
		"TRUE / FALSE.",
	),
];

const PRODUCT_NON_CONSIGNMENT_KEYS: UploadStandardKey[] = [
	...PRODUCT_IMPORT_IDENTITY_KEYS,
	...PRODUCT_IMPORT_CLASSIFICATION_KEYS,
	productKey(
		"produced",
		"Produced",
		"optional (default FALSE)",
		"TRUE / FALSE.",
	),
	productKey(
		"canBePurchased",
		"Can Be Purchased",
		"optional (default FALSE)",
		"TRUE / FALSE.",
	),
	productKey("purchaseCost", "Purchased Cost", "optional"),
	productKey("minimumOrder", "Minimum Order", "optional"),
	productKey(
		"purchaseUom",
		"Purchase UoM",
		"mandatory if Can Be Purchased is TRUE",
	),
	productKey(
		"purchaseTax",
		"Purchase Tax",
		"mandatory if Can Be Purchased is TRUE",
	),
	productKey(
		"canBeSold",
		"Can Be Sold",
		"optional (default TRUE)",
		"TRUE / FALSE.",
	),
	productKey(
		"sellingPrice",
		"Default Selling Price",
		"mandatory if Can Be Sold is TRUE",
	),
	productKey("salesUom", "Sales UoM", "optional (default Base UoM)"),
	productKey("salesTax", "Sales Tax", "mandatory if Can Be Sold is TRUE"),
	productKey(
		"salesScanType",
		"Sales Scan Type",
		"optional (default Barcode)",
		"Barcode, Scale Image or Image.",
	),
	...PRODUCT_IMPORT_TRAILING_KEYS,
];

const PRODUCT_CONSIGNMENT_KEYS: UploadStandardKey[] = [
	...PRODUCT_IMPORT_IDENTITY_KEYS,
	productKey(
		"vendorCode",
		"Vendor Code",
		NEW_PRODUCT_NOTE,
		"Mandatory for a new product. Cannot be changed on an existing product.",
	),
	...PRODUCT_IMPORT_CLASSIFICATION_KEYS,
	productKey(
		"sellingPrice",
		"Default Selling Price",
		NEW_PRODUCT_NOTE,
		"When filled it becomes the price in every branch of the vendor.",
	),
	productKey(
		"commissionPerc",
		"Commission %",
		"optional (default 0)",
		"0–100. When filled it becomes the commission in every branch of the vendor.",
	),
	productKey("salesTax", "Sales Tax", NEW_PRODUCT_NOTE, NEW_PRODUCT_HINT),
	productKey(
		"salesScanType",
		"Sales Scan Type",
		"optional (default Barcode)",
		"Barcode, Scale Image or Image.",
	),
	...PRODUCT_IMPORT_TRAILING_KEYS,
];

const SALES_CHANNEL_CATEGORY_MAPPING_KEYS: UploadStandardKey[] = [
	{
		key: "productCategoryCode",
		label: "Product Category Code",
		required: true,
		hint: "Resolved via the product category NAME (there is no code column). Unknown categories are rejected.",
	},
	{
		key: "productCategoryName",
		label: "Product Category Name",
		required: false,
		hint: "Optional, human reference only. The code column is what resolves the category.",
	},
	{
		key: "externalCategoryID",
		label: "External Category ID",
		required: true,
		hint: "The channel leaf category ID. Must differ from the parent (a sub-category must be a real leaf).",
	},
	{
		key: "externalCategoryName",
		label: "External Category Name",
		required: true,
		hint: "The channel leaf category name.",
	},
	{
		key: "externalParentCategoryID",
		label: "External Parent Category ID",
		required: true,
		hint: "The channel parent category ID.",
	},
	{
		key: "externalParentCategoryName",
		label: "External Parent Category Name",
		required: false,
		hint: "The channel parent category name.",
	},
	{
		key: "isActive",
		label: "Is Active",
		required: false,
		hint: "true / false. Defaults to active when blank.",
		includeInTemplate: true,
	},
];

export const IMPORT_TEMPLATE_TYPE_CONFIG: Record<string, ImportTemplateConfig> =
	{
		bank_recon: {
			standardKeys: RECON_BANK_KEYS,
			pageTitle: "Import Bank Statement",
			uploadHeading: "Upload Bank Statement",
			uploadSubheading:
				"Pick the source of this file. We'll match every transaction against your sales invoice payments.",
			uploadSourceLabel: "Bank source",
			newSourceLabel: "+ New source (configure mapping)",
			continueLabelExisting: "Upload & Reconcile",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle: "The recon engine can't run without these.",
			sourceNamePlaceholder: "e.g. BNI EDC",
			completeLabel: "Reconcile",
			HeaderIcon: BanknoteIcon,
			onCompleteToast: "Reconciliation kicked off — backend wiring TBD",
			returnTo: "/admin/recon-bank",
		},
		transfer_order: {
			standardKeys: TRANSFER_ORDER_KEYS,
			pageTitle: "Import Transfer Orders",
			uploadHeading: "Upload Transfer Orders",
			uploadSubheading:
				"Bulk-create TOs from a spreadsheet. One TO per Branch Code row; rows below with an empty Branch Code become items of the same TO. Branch / warehouse references resolve by code, products by barcode.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Import",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle:
				"The importer can't create the TO header without these.",
			sourceNamePlaceholder: "e.g. ERP TO Export",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Transfer orders queued — backend wiring TBD",
			returnTo: "/admin/transfer-order",
		},
		transfer_request: {
			standardKeys: TRANSFER_REQUEST_KEYS,
			pageTitle: "Import Transfer Requests",
			uploadHeading: "Upload Transfer Requests",
			uploadSubheading:
				"Bulk-create TRs from a spreadsheet. One TR per Branch Code row; rows below with an empty Branch Code become items of the same TR. Branch / warehouse references resolve by code, products by barcode. No cost — TR is a store request to the DC.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Import",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle:
				"The importer can't create the TR header without these.",
			sourceNamePlaceholder: "e.g. Store TR Export",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Transfer requests queued — created in the background.",
			returnTo: "/admin/transfer-request",
		},
		product_availability_template_item: {
			standardKeys: PRODUCT_AVAILABILITY_TEMPLATE_ITEM_KEYS,
			pageTitle: "Import Product Availability Items",
			uploadHeading: "Upload Product Availability Items",
			uploadSubheading:
				"Bulk-add products to this availability template from a spreadsheet. Each row = one product, resolved by product code. Products already in the template are skipped.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Import",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle: "Product Code is the only required column.",
			sourceNamePlaceholder: "e.g. Product Codes Export",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Products added to template",
			returnTo: "/admin/product-availability-template",
		},
		sales_channel_category_mapping: {
			standardKeys: SALES_CHANNEL_CATEGORY_MAPPING_KEYS,
			pageTitle: "Import Category Mapping",
			uploadHeading: "Upload Category Mapping",
			uploadSubheading:
				"Bulk-map product categories to a sales channel's taxonomy from a spreadsheet. Each row = one product category → channel category. Existing rows for the category are upserted.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Import",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle:
				"Product Category Code, External Category ID/Name, and External Parent Category ID are required.",
			sourceNamePlaceholder: "e.g. GrabMart Category Map",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Category mappings imported",
			returnTo: "/admin/sales-channel-category-mapping",
		},
		inventory_count_item_template_item: {
			standardKeys: INVENTORY_COUNT_ITEM_TEMPLATE_ITEM_KEYS,
			pageTitle: "Import Inventory Count Items",
			uploadHeading: "Upload Inventory Count Items",
			uploadSubheading:
				"Bulk-add products to this inventory count template from a spreadsheet. Each row = one product, resolved by product code. Products already in the template are skipped.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Import",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle: "Product Code is the only required column.",
			sourceNamePlaceholder: "e.g. Product Codes Export",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Products added to template",
			returnTo: "/admin/inventory-count-template-item",
		},
		promo_component: {
			standardKeys: PROMO_COMPONENT_KEYS,
			pageTitle: "Import Promo Products",
			uploadHeading: "Upload Promo Products",
			uploadSubheading:
				"Fill one product table of a promo component from a spreadsheet. Each row = one product, resolved by barcode. The file says nothing about which table it fills — that is decided by the Import button you clicked.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Continue",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle: "Barcode alone identifies the product.",
			sourceNamePlaceholder: "e.g. Promo Product List",
			completeLabel: "Fill Table",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Products filled in",
			// The wizard lives in a dialog over the promo form and never
			// navigates; this only satisfies the shared config shape.
			returnTo: "/admin/promo",
		},
		pricelist_product: {
			standardKeys: PRICELIST_PRODUCT_KEYS,
			pageTitle: "Import Pricelist Products",
			uploadHeading: "Upload Pricelist Products",
			uploadSubheading:
				"Bulk-create or update pricelist items from a spreadsheet. Each row = one product, resolved by product code. Rows for products already in this pricelist period overwrite the existing selling price.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Import",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle: "Product Code + Selling Price keep the import lean.",
			sourceNamePlaceholder: "e.g. ERP Pricelist Export",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Pricelist items imported",
			returnTo: "/admin/pricelist",
		},
		replenishment_template_item: {
			standardKeys: REPLENISHMENT_TEMPLATE_ITEM_KEYS,
			pageTitle: "Import Replenishment Items",
			uploadHeading: "Upload Replenishment Items",
			uploadSubheading:
				"Bulk-create replenishment template items from a spreadsheet. Each row = one product, resolved by product code.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Import",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle:
				"Min Stock, Multiplier, Leadtime and Safety days are required for the auto-PO engine to work.",
			sourceNamePlaceholder: "e.g. ERP Replenishment Export",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Items imported",
			returnTo: "/admin/replenishment-template",
		},
		inventory_adjustment_opening_balance: {
			standardKeys: INVENTORY_ADJUSTMENT_OPENING_BALANCE_KEYS,
			pageTitle: "Import Opening Balance",
			uploadHeading: "Upload Opening Balance",
			uploadSubheading:
				"Bulk-create the initial stock IADJ for one warehouse from a spreadsheet. Each row = one (product × location) line. Products resolve by barcode; uom / location references resolve by name.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Import",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle:
				"Barcode, UoM, Qty and Location Name are required for opening-balance to compute.",
			sourceNamePlaceholder: "e.g. ERP Opening Balance Export",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Opening balance import queued",
			returnTo: "/admin/inventory-adjustment",
		},
		inventory_adjustment_cost: {
			standardKeys: INVENTORY_ADJUSTMENT_COST_KEYS,
			pageTitle: "Import Cost Adjustment",
			uploadHeading: "Upload Cost Adjustment",
			uploadSubheading:
				"Correct the average unit cost of products in one warehouse from a spreadsheet. Each row = one product's target unit cost. On-hand qty is not changed.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Import",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle: "Barcode and Unit Cost are required.",
			sourceNamePlaceholder: "e.g. Cost Correction Export",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Cost adjustment import queued",
			returnTo: "/admin/inventory-adjustment",
		},
		product_non_consignment: {
			standardKeys: PRODUCT_NON_CONSIGNMENT_KEYS,
			pageTitle: "Import Non Consignment Products",
			uploadHeading: "Upload Non Consignment Products",
			uploadSubheading:
				"Create and update non-consignment products from a spreadsheet. A row with Product Code updates that product; without it, the barcode decides: unknown barcodes create a new product, a known barcode updates its product. Empty cells keep the stored value.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Review",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle:
				"Map Barcode, or Product Code for a file that only updates products.",
			sourceNamePlaceholder: "e.g. Product master template",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Product import queued.",
			returnTo: "/admin/product",
		},
		product_consignment: {
			standardKeys: PRODUCT_CONSIGNMENT_KEYS,
			pageTitle: "Import Consignment Products",
			uploadHeading: "Upload Consignment Products",
			uploadSubheading:
				"Create and update consignment products from a spreadsheet. Each row names its vendor by Vendor Code. A row with Product Code updates that product; without it, the barcode decides. Empty cells keep the stored value.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Review",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle:
				"Map Barcode, or Product Code for a file that only updates products.",
			sourceNamePlaceholder: "e.g. Consignment product template",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast: "Product import queued.",
			returnTo: "/admin/product",
		},
		purchase_order: {
			standardKeys: PURCHASE_ORDER_KEYS,
			pageTitle: "Import Purchase Orders",
			uploadHeading: "Upload Purchase Orders",
			uploadSubheading:
				"Bulk-create POs from a spreadsheet. One PO per Vendor Code row; rows below with an empty Vendor Code become items of the same PO. All vendor / branch / warehouse / tax references resolve by code.",
			uploadSourceLabel: "Source format",
			newSourceLabel: "+ New format (configure mapping)",
			continueLabelExisting: "Upload & Import",
			continueLabelNew: "Continue to Mapping",
			requiredSubtitle:
				"The importer can't create the PO header without these.",
			sourceNamePlaceholder: "e.g. ERP PO Export",
			completeLabel: "Import",
			HeaderIcon: FileSpreadsheetIcon,
			onCompleteToast:
				"Purchase orders queued — backend wiring TBD (step 2 of feature)",
			returnTo: "/admin/purchase-order",
		},
	};

export const getImportTemplateConfig = (
	templateType: string,
): ImportTemplateConfig | undefined =>
	IMPORT_TEMPLATE_TYPE_CONFIG[templateType];

export type ImportCompletionInput = {
	file: File;
	template: UploadTemplateOption;
};
